import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { toast } from 'sonner';
import { supabase } from '@/lib/supabase';
import { useAuth } from '@/app/providers/AuthProvider';
import type { Database } from '@/types/database';
import type {
  CompletionWithStep,
  OnboardingFlow,
  OnboardingStepCompletion,
  OnboardingStepTemplate,
} from './types';

type CompletionUpdate = Database['public']['Tables']['onboarding_step_completions']['Update'];

interface CompletionRowJoin extends OnboardingStepCompletion {
  step?: OnboardingStepTemplate | OnboardingStepTemplate[] | null;
}

export function useOnboardingForClient(clientId: string | null) {
  return useQuery({
    queryKey: ['onboarding', clientId],
    enabled: !!clientId,
    staleTime: 30_000,
    queryFn: async (): Promise<{
      flow: OnboardingFlow | null;
      completions: CompletionWithStep[];
    }> => {
      if (!clientId) return { flow: null, completions: [] };

      const { data: flow, error: flowErr } = await supabase
        .from('onboarding_flows')
        .select('*')
        .eq('client_id', clientId)
        .maybeSingle();
      if (flowErr) throw flowErr;
      if (!flow) return { flow: null, completions: [] };

      const { data: rows, error } = await supabase
        .from('onboarding_step_completions')
        .select('*, step:onboarding_step_templates(*)')
        .eq('flow_id', flow.id);
      if (error) throw error;

      const completions: CompletionWithStep[] = (rows as CompletionRowJoin[])
        .map((row) => {
          const stepObj = Array.isArray(row.step) ? row.step[0] : row.step;
          if (!stepObj) return null;
          const { step: _omit, ...rest } = row;
          void _omit;
          return { ...rest, step: stepObj };
        })
        .filter((x): x is CompletionWithStep => x !== null)
        .sort((a, b) => a.step.order_index - b.step.order_index);

      return { flow, completions };
    },
  });
}

export function useUpdateCompletion() {
  const qc = useQueryClient();
  const { user } = useAuth();
  return useMutation({
    mutationFn: async (params: {
      completionId: string;
      status: 'pending' | 'completed' | 'skipped';
      notes?: string | null;
    }) => {
      const updates: CompletionUpdate = {
        status: params.status,
        notes: params.notes ?? null,
      };
      if (params.status === 'completed') {
        updates.completed_at = new Date().toISOString();
        updates.completed_by_user_id = user?.id ?? null;
      } else {
        updates.completed_at = null;
        updates.completed_by_user_id = null;
      }
      const { error } = await supabase
        .from('onboarding_step_completions')
        .update(updates)
        .eq('id', params.completionId);
      if (error) throw error;
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['onboarding'] });
      qc.invalidateQueries({ queryKey: ['clients'] });
    },
    onError: (e: Error) => toast.error('Erro ao atualizar passo: ' + e.message),
  });
}

export function useGenerateFormToken() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (params: {
      flowId: string;
      stepTemplateId: string;
      expiresInDays?: number;
    }): Promise<{ token: string; url: string; expires_in_days: number }> => {
      const { data, error } = await supabase.rpc('create_onboarding_form_token', {
        p_flow_id: params.flowId,
        p_step_template_id: params.stepTemplateId,
        p_expires_in_days: params.expiresInDays ?? 14,
      });
      if (error) throw error;
      const result = data as { token: string; expires_in_days: number };
      const url = `${window.location.origin}/onboarding/${result.token}`;
      return { ...result, url };
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['onboarding'] });
    },
    onError: (e: Error) => toast.error('Erro ao gerar link: ' + e.message),
  });
}
